import React, { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalFooter } from './ui/modal';
import { Button } from './ui/button';
import { X } from 'lucide-react';

interface LegalModalProps {
  type: 'terms' | 'privacy' | 'refund';
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const termsContent = `
# Terms of Service

**Last updated: December 2024**

## 1. Acceptance of Terms
By accessing and using HiPo AI Coach ("the Service"), you accept and agree to be bound by the terms and provision of this agreement.

## 2. Description of Service
HiPo AI Coach is an AI-powered coaching platform that provides personalized professional development guidance through various coaching personas.

## 3. User Responsibilities
- You are responsible for maintaining the confidentiality of your account
- You agree to provide accurate and complete information
- You will not use the service for any unlawful purposes
- You will not attempt to gain unauthorized access to the service

## 4. Privacy and Data Protection
Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your information.

## 5. Intellectual Property
All content, features, and functionality of the Service are owned by HiPo AI Coach and are protected by copyright, trademark, and other intellectual property laws.

## 6. Limitation of Liability
The Service is provided "as is" without warranties of any kind. We shall not be liable for any indirect, incidental, special, or consequential damages.

## 7. Termination
We may terminate or suspend your account at any time for violations of these terms.

## 8. Changes to Terms
We reserve the right to modify these terms at any time. Continued use of the Service constitutes acceptance of modified terms.

## 9. Contact Information
For questions about these Terms of Service, please contact us at contactus@hipoaicoach.com.
`;

const refundContent = `
# RETURN & REFUND POLICY

*Updated at January 17th, 2026*

## Definitions and key terms
To help explain things as clearly as possible in this Return & Refund Policy, every time any of these terms are referenced, are strictly defined as:

- **Company:** when this policy mentions “Company”, “we”, “us” or “our”, it refers to HiPo AI Coach, that is responsible for your information under this Return & Refund Policy.
- **Customer:** refers to the company, organization or person that signs up to use the HiPo AI Coach Service to manage the relationships with your consumers or service users.
- **Device:** any internet connected device such as a phone, tablet, computer or any other device that can be used to visit HiPo AI Coach and use the services.
- **Service:** refers to the service provided by HiPo AI Coach as described in the relative terms (if available) and on this platform.
- **Website:** HiPo AI Coach’s site, which can be accessed via this URL: hipoaicoach.com
- **You:** a person or entity that is registered with HiPo AI Coach to use the Services.

## Return & Refund Policy
Thanks for shopping at HiPo AI Coach. We appreciate the fact that you like to buy the stuff we create. We also want to make sure you have a rewarding experience while you’re exploring, evaluating, and purchasing our products.

As with any shopping experience, there are terms and conditions that apply to transactions at HiPo AI Coach. We’ll be as brief as our attorneys will allow. The main thing to remember is that by placing an order or making a purchase at HiPo AI Coach, you agree to the terms set forth below along with HiPo AI Coach’s Privacy Policy.

If there’s something wrong with the you bought, or if you are not happy with it, you will not be able to issue a refund for your item.

## Refunds
We at HiPo AI Coach commit ourselves to serving our customers with the best products. Every single product that you choose is thoroughly inspected, checked for defects and packaged with utmost care. We do this to ensure that you fall in love with our products.

Sadly, there are times when our systems and servers might cause issues. In such cases, we may have to cancel your order. You will be intimated about it in advance so that you don't have to worry unnecessarily about your order. If you have purchased via Online payment (not Cash on Delivery), then you will be refunded once our team confirms your request.

The following are our policy on refunds on subscriptions or services purchased on our webapp:

- We DO NOT allow returns on sold products - online or in retail outlets.
- We DO NOT accept returned goods, as we believe that customers should get the best quality products.
- Refunds are NOT given for any purchases made - be they online or in retail store.
- We DO NOT encourage exchanges of our products.
- We DO NOT engage in reselling used products and discourage the same, because we cannot ensure the best quality products for our customers.

### For International Orders:
- We DO NOT support Exchanges or Returns.
- If you cancel the order before we process it and dispatch for shipping, a refund can be processed. Orders generally take 1-2 days to process before dispatch.
- Orders already in shipping cannot be returned, canceled or refunded.
- If you face any issues, please contact our Support Team immediately.

## Your Consent
By using our website, registering an account, or making a purchase, you hereby consent to our Return & Refund Policy and agree to its terms.

## Changes To Our Return & Refund Policy
Should we update, amend or make any changes to this document so that they accurately reflect our Service and policies. Unless otherwise required by law, those changes will be prominently posted here. Then, if you continue to use the Service, you will be bound by the updated Return & Refund Policy. If you do not want to agree to this or any updated Return & Refund Policy, you can delete your account.

## Contact Us
If, for any reason, You are not completely satisfied with any good or service that we provide, don't hesitate to contact us and we will discuss any of the issues you are going through with our product.

- Via Email: <a href="mailto:contactus@hipo.co.in">contactus@hipo.co.in</a>
`;

const privacyContent = `
# Privacy Policy

**Last updated: December 2024**

## 1. Information We Collect
We collect information you provide directly to us, such as when you create an account, use our services, or contact us.

### Personal Information:
- Name and email address
- Professional information (role, industry, experience)
- Coaching session data and preferences
- Usage analytics and interaction data

## 2. How We Use Your Information
- To provide and improve our coaching services
- To personalize your coaching experience
- To communicate with you about the service
- To analyze usage patterns and improve our platform

## 3. Information Sharing
We do not sell, trade, or otherwise transfer your personal information to third parties except:
- With your explicit consent
- To comply with legal obligations
- To protect our rights and safety

## 4. Data Security
We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.

## 5. Data Retention
We retain your personal information for as long as necessary to provide our services and comply with legal obligations.

## 6. Your Rights
You have the right to:
- Access your personal information
- Correct inaccurate information
- Delete your account and associated data
- Export your data

## 7. Cookies and Tracking
We use cookies and similar technologies to enhance your experience and analyze usage patterns.

## 8. Third-Party Services
Our service may integrate with third-party services (authentication, analytics) that have their own privacy policies.

## 9. Changes to Privacy Policy
We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page.

## 10. Contact Us
If you have questions about this Privacy Policy, please contact us at privacy@hipoaicoach.com.
`;

export function LegalModal({ type, open, onOpenChange }: LegalModalProps) {
  const content = type === 'terms' ? termsContent : type === 'refund' ? refundContent : privacyContent;
  const title = type === 'terms' ? 'Terms of Service' : type === 'refund' ? 'Return & Refund Policy' : 'Privacy Policy';

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <ModalHeader className="flex items-center justify-between border-b pb-4">
          <ModalTitle>{title}</ModalTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </ModalHeader>
        
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="prose prose-sm max-w-none">
            {content.split('\n').map((line, index) => {
              if (line.startsWith('# ')) {
                return <h1 key={index} className="text-xl font-bold mb-4 mt-6 first:mt-0">{line.slice(2)}</h1>;
              }
              if (line.startsWith('## ')) {
                return <h2 key={index} className="text-lg font-semibold mb-3 mt-5">{line.slice(3)}</h2>;
              }
              if (line.startsWith('### ')) {
                return <h3 key={index} className="text-base font-medium mb-2 mt-4">{line.slice(4)}</h3>;
              }
              if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={index} className="font-semibold mb-2">{line.slice(2, -2)}</p>;
              }
              if (line.startsWith('- ')) {
                return <ul key={index} className="mb-2"><li className="mb-1 ml-4">{line.slice(2)}</li></ul>;
              }
              if (line.trim() === '') {
                return <div key={index} className="mb-2" />;
              }
              return <p key={index} className="mb-2 leading-relaxed">{line}</p>;
            })}
          </div>
        </div>
        
        <ModalFooter>
          <Button onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

interface LegalLinkProps {
  type: 'terms' | 'privacy' | 'refund';
  children: React.ReactNode;
  className?: string;
}

export function LegalLink({ type, children, className = "text-primary underline" }: LegalLinkProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={className}
      >
        {children}
      </button>
      <LegalModal type={type} open={isOpen} onOpenChange={setIsOpen} />
    </>
  );
}
